import { memo, useCallback, useMemo } from 'react';

import { Icon, Link, Markdown, Popover, Switch, Text, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const RE_ENTRY_POPOVER_OPTIONS = [
    {
        titleKey: 'label_automation_re_entry_off',
        descKey: 'label_automation_re_entry_off_desc',
    },
    {
        titleKey: 'label_automation_re_entry_on',
        descKey: 'label_automation_re_entry_on_desc',
    },
];

const AutomationBuilderReEntryRow = memo(
    ({ checked = false, interactive = true, isDisabled = false, onChange, variant = 'canvas' }) => {
        const theme = useTheme();

        const onSwitchChange = useCallback(
            (nextChecked) => {
                if (!interactive || isDisabled || nextChecked === checked) return;
                onChange?.(nextChecked);
            },
            [checked, interactive, isDisabled, onChange],
        );

        const markdownOverrides = useMemo(
            () => ({
                a: { target: '_blank' },
                p: {
                    type: 'caption',
                    color: theme.colors.textLight.secondary,
                },
            }),
            [theme.colors.textLight.secondary],
        );

        const infoPopoverProps = useMemo(
            () => ({
                trigger: ['hover'],
                overlayStyle: { zIndex: 9999999 },
                content: (
                    <div className="fm-automation-builder__re-entry-row__popover">
                        {RE_ENTRY_POPOVER_OPTIONS.map(({ titleKey, descKey }) => (
                            <div
                                key={titleKey}
                                className="fm-automation-builder__re-entry-row__popover-option"
                            >
                                <Text type="subtitle" medium>
                                    {getLiteral(titleKey)}
                                </Text>
                                <Markdown
                                    content={getLiteral(descKey)}
                                    overrides={markdownOverrides}
                                />
                            </div>
                        ))}
                        <Link
                            className="fm-automation-builder__re-entry-row__learn-more"
                            href={getLiteral('label_automations_re_entry_knowledge_base_link')}
                            target="_blank"
                            type="caption"
                            variation="primary"
                        >
                            {getLiteral('label_learn_more')}
                        </Link>
                    </div>
                ),
            }),
            [markdownOverrides],
        );

        const stopPropagation = useCallback((e) => {
            e.stopPropagation();
        }, []);

        return (
            <div
                className={classnames('fm-automation-builder__re-entry-row', {
                    'fm-automation-builder__re-entry-row--readonly': !interactive,
                    'fm-automation-builder__re-entry-row--panel': variant === 'panel',
                })}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
            >
                <div className="fm-automation-builder__re-entry-row__label">
                    <Icon
                        className="fm-automation-builder__re-entry-row__icon"
                        name="autorenew"
                        size="smallMedium"
                    />
                    <Text medium>
                        {getLiteral(
                            checked
                                ? 'action_automation_re_entry_on'
                                : 'action_automation_re_entry_off',
                        )}
                    </Text>
                    <span
                        className="fm-automation-builder__re-entry-row__info"
                        onClick={stopPropagation}
                    >
                        <Popover {...infoPopoverProps}>
                            <span>
                                <Icon name="info" size="small" />
                            </span>
                        </Popover>
                    </span>
                </div>
                <div className="fm-automation-builder__re-entry-row__switch">
                    <Switch
                        checked={checked}
                        isDisabled={isDisabled}
                        onChange={onSwitchChange}
                        size="medium"
                    />
                </div>
            </div>
        );
    },
);

AutomationBuilderReEntryRow.displayName = 'AutomationBuilderReEntryRow';

export default AutomationBuilderReEntryRow;
